import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import posed from 'react-pose'
import { Loading, SVGWrapper } from '../../../components'
import { IpmArrowRight } from '../../../assets/icons'
import logo from '../../../assets/images/GuidedChoice-logo.png'
import ContactArt from '../../../assets/images/contact-us.png'
import AdvisorArt from '../../../assets/images/3NUAdvisor.png'
import { MOBILE, TABLET } from '../../../guided-toolbox/utils/breakpoints'

const CONTACT_URL = 'https://guidedchoice.com/contact/'

const Page = styled.div`
  max-width: 1300px;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

Page.Container = styled.div`
  padding: 16px;
  position: relative;
  width: 100%;
  color: ${p => p.theme.offboardingPageContainerTextColor};
`

Page.Column = styled.div`
  display: flex;
  flex-direction: column;
`

Page.Loading = () => (
  <div
    css={css`
      margin: 20px;
    `}>
    <Loading />
  </div>
)

export const Header = props => {
  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        max-width: 960px;
        margin: auto;
        min-height: 75px;
        @media (max-width: 800px) {
          justify-content: center;
          min-height: 0;
        }
      `}>
      {props.organizationLogo && (
        <img
          css={css`
            height: 48px;
            width: auto;
          `}
          src={props.organizationLogo}
          alt='Organization Logo'
        />
      )}
      {/* On desktop the GC logo sits at the right of the header row. On mobile it is
          hidden here and re-rendered, centered, at the bottom of the page (Page.BottomLogo). */}
      <a
        href='https://www.guidedchoice.com'
        target='_blank'
        rel='noopener noreferrer'
        css={css`
          max-width: 220px;
          padding: 2px 10px;
          @media (max-width: 800px) {
            display: none;
          }
        `}>
        <img
          css={css`
            width: 100%;
          `}
          src={logo}
          alt='GuidedChoice'
        />
      </a>
    </div>
  )
}

// GC logo shown only on mobile, horizontally centered at the bottom of the page.
Page.BottomLogo = () => (
  <a
    href='https://www.guidedchoice.com'
    target='_blank'
    rel='noopener noreferrer'
    css={css`
      display: none;
      @media (max-width: 800px) {
        display: block;
        margin: 40px auto;
      }
    `}>
    <img
      src={logo}
      alt='GuidedChoice'
      css={css`
        display: block;
        width: 220px;
        max-width: 60vw;
        margin: auto;
      `}
    />
  </a>
)

Page.Text = styled.div`
  font-size: 1.125rem;
  color: ${p => p.theme.mediumGray};
  line-height: 1.35;
`

Page.LightText = styled.div`
  font-size: 1.125rem;
  color: ${p => p.theme.lightestGray};
  line-height: 1.35;
`

Page.WhoPaysText = styled.div`
  font-size: 14px;
  color: ${p => p.theme.lightestGray};
`
Page.PricebookNameText = styled.div`
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 30px;
  font-weight: light;
  color: ${p => p.theme.mediumGray};
  @media (max-width: 800px) {
    font-size: 20px;
    font-weight: 600;
  }
`

Page.WelcomeHeading = styled.div`
  text-align: center;
  max-width: 679px;
  margin: auto;
  padding: 20px 0 40px;
  font-family: 'Aleo', 'Helvetica Neue', sans-serif;
  font-size: 3.125rem;
  font-weight: bold;
  word-wrap: break-word;
  color: ${p => p.theme.darkestGray};
`

Page.WelcomeParagraph = styled(Page.Text)`
  margin: 20px auto;
  max-width: 679px;
`

Page.TermsSubText = styled(Page.Text)`
  font-size: 1rem;
  max-width: 679px;
  margin: auto;
  padding-left: 64px;
`

Page.AnimateHeading = posed.div({
  enter: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -15 },
})

const HeadingStyles = styled.div`
  width: 100%;
  display: flex;
  flex-basis: 100%;
  padding-top: 10px;
  margin-bottom: 30px;
  max-width: 1200px;
  align-self: flex-start;
  @media (min-width: 1200px) {
    margin-bottom: 40px;
  }

  /* Title */
  & > div:first-of-type {
    font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
    font-size: 3rem;
    width: 100%;
    color: ${p => p.theme.ssiHeadingTextColor};
    transition: 0.75s;
    font-weight: 300;
    line-height: 1.25;
    word-break: break-word;
    @media (max-width: 800px) {
      font-size: 2rem;
      /* max-width: 400px; */
    }
    @media (min-width: 1200px) {
      font-size: 3.125rem;
      /* max-width: 1200px; */
    }
  }

  /* SubTitle */
  & > div:nth-of-type(2) {
    color: ${p => p.theme.lightestGray};
    font-size: 1rem;
    padding-top: 1rem;
    line-height: 20px;
  }
`

Page.SubHeading = styled.div`
  display: flex;
  max-width: 1200px;
  align-self: flex-start;

  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 20px;
  color: ${p => p.theme.lightestGray};
  transition: 0.75s;
  font-weight: 300;
  line-height: 1.25;
  word-break: break-word;
`

Page.PricebookSubHeading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;

  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 20px;
  color: ${p => (p.selected ? 'white' : p.theme.mediumGray)};
  font-weight: 600;
  line-height: 1.25;
  word-break: break-word;

  /* Per-run price styling: gray currency glyph, dark digits, slate interval. */
  & .currency {
    color: ${p => p.theme.lightestGray};
  }
  & .amount {
    color: ${p => p.theme.darkestGray};
  }
  & .interval {
    font-size: 16px;
    font-weight: 400;
    color: ${p => p.theme.mediumGray};
  }
  & .asterisk {
    font-size: 16px;
    font-weight: 400;
    color: ${p => p.theme.mediumGray};
  }
`

const BulletTextStyles = styled.div`
  display: flex;
  color: ${p => (p.selected ? 'white' : '')};
`
Page.Heading = props => {
  const { title, children, subTitle } = props
  return (
    <HeadingStyles>
      <div>{title || children}</div>
      {subTitle && <div>{subTitle}</div>}
    </HeadingStyles>
  )
}

Page.BulletText = props => {
  const { children, selected } = props
  return (
    <BulletTextStyles selected={selected}>
      <div
        css={css`
          margin-right: 20px;
        `}>
        •
      </div>
      <div>{children}</div>
    </BulletTextStyles>
  )
}

Page.PackageBulletText = styled.div`
  font-size: 16px;
  color: ${p => p.theme.lightestGray};
`
Page.DescriptionTitleText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.theme.mediumGray};
  margin-bottom: 10px;
`
Page.DescriptionText = styled.div`
  font-size: 16px;
  font-weight: normal;
  color: ${p => p.theme.lightestGray};
`

Page.OrgPaysText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.mediumGray};
  display: flex;
  max-width: 1200px;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  line-height: 1.25;
  word-break: break-word;
  justify-content: center;
`

Page.Link = styled.a`
  cursor: pointer;
  color: ${p => p.theme.customLinkColor};
  text-decoration: underline;
`

Page.FooterNote = styled.span`
  font-size: 16px;
  line-height: 24px;
`

Page.Form = styled.form`
  margin: 20px 0;
  width: 100%;
`

const FieldStyles = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  /* Field Label Container */
  & > div:first-of-type {
    flex: 1 1 340px;
    font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
    text-align: right;
    margin-right: 40px;
    color: ${p => p.theme.lightestGray};
    font-size: 1.125rem;
    line-height: 1.35;

    /* Field Label */
    & > div:first-of-type {
    }

    /* Field SubLabel (Optional) */
    & > div:nth-of-type(2) {
      font-size: 0.875rem;
    }
  }

  /* Field Input Container */
  & > div:nth-of-type(2) {
    flex: 1 1 340px;
    font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
    color: ${p => p.theme.mediumGray};
    font-size: 1.125rem;
    line-height: 1.35;
    word-break: break-word;
  }

  @media (max-width: 800px) {
    flex-wrap: wrap;
    margin: 20px 0;
    & > div:first-of-type {
      flex: 1 1 100%;
      text-align: left;
      margin: 12px 0;
    }
  }
`
Page.Field = props => {
  const { label, subLabel, children, ...rest } = props
  return (
    <FieldStyles {...rest}>
      <div>
        <div
          css={css`
            line-height: min(54px);
          `}>
          {label}
        </div>
        {subLabel && (
          <div
            css={css`
              line-height: max(10px);
              margin-bottom: 10px;
            `}>
            {subLabel}
          </div>
        )}
      </div>
      <div
        css={css`
          line-height: min(54px);
        `}>
        {children}
      </div>
    </FieldStyles>
  )
}

Page.ErrorMessage = styled.div`
  color: crimson;
  text-align: center;
  margin: 20px 0;
`

Page.BundleCard = styled.div`
  width: 100%;
  max-width: 690px;
  box-sizing: border-box;
  color: ${p => p.theme.darkestGray};
  /* primaryColor at 5% opacity (0x0d = 13/255 ≈ 0.05). */
  background-color: ${p => `${p.theme.primaryColor ?? '#038ab7'}0d`};
  border: none;
  border-radius: 7px;
  display: flex;
  /* Mobile: vertical stack (art → bullets → price). */
  flex-direction: column;
  align-items: center;
  padding: 30px 10px 10px;
  /* Desktop: horizontal row (art left, bullets middle, price card right). */
  @media (min-width: 801px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 20px;
  }
`
Page.InnerCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border: none;
  border-radius: 10px;
  /* donutPlanColor at 20% opacity (0.2 * 255 = 0x33 hex alpha). */
  background-color: ${p => `${p.theme.donutPlanColor ?? '#4fadcc'}33`};
  color: ${p => p.theme.mediumGray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px 0;
`

Page.AnimateGroup = posed.div({
  enter: { staggerChildren: 50 },
  exit: { staggerChildren: 20, staggerDirection: -1 },
})

Page.AnimateItem = posed.div({
  enter: { y: 0, opacity: 1 },
  exit: { y: 20, opacity: 0 },
})

Page.Animate = posed.div({
  visible: { y: 0, opacity: 1 },
  hidden: { y: 20, opacity: 0 },
})

/* ------------------------------------------------------------------ *
 * Welcome / product-selection screen (redesign)                      *
 * ------------------------------------------------------------------ */

// Constrains the welcome content to a narrower, centered column than the rest
// of retail (which uses the 1300px base Page). Falls back to 20px gutters on mobile.
Page.WelcomeContent = styled.div`
  width: 100%;
  max-width: 960px;
  margin: auto;
  padding: 0 20px;
  box-sizing: border-box;
  margin-top: 65px;
  @media (max-width: 800px) {
    margin-top: 30px;
  }
`

Page.WelcomeTitle = styled.div`
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 45px;
  font-weight: normal;
  color: ${p => p.theme.mediumGray};
  @media (max-width: 800px) {
    font-size: 24px;
    text-align: center;
    color: ${p => p.theme.darkestGray};
  }
`

// The organization's custom sign-up blurb (organization-metadata.signupPageText).
// Held to 70% of the content column on desktop for readability; goes full width and
// centers on mobile, where the column is already narrow.
Page.WelcomeBlurb = styled.div`
  font-size: 16px;
  line-height: 22px;
  color: ${p => p.theme.mediumGray};
  margin-top: 10px;
  width: 70%;
  white-space: pre-line;
  @media (max-width: 800px) {
    width: 100%;
    text-align: center;
  }
`

// "Already have an account? Log in" — the lead-in is gray, only "Log in" is blue.
Page.LoginPrompt = styled.div`
  font-size: 16px;
  color: ${p => p.theme.lightestGray};
  margin-top: 10px;
  @media (max-width: 800px) {
    text-align: center;
    margin-top: 20px;
  }
`

// Footnote / legal copy under the cards: gray base text, links inherit Page.Link.
Page.FootnoteGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 30px;
  width: 100%;
  color: ${p => p.theme.lightestGray};
  font-size: 14px;
  line-height: 20px;
`

// Row of product cards: always horizontally centered; wraps to a single column on mobile.
Page.PlanRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 72px;
  justify-content: center;
  width: 100%;
  ${TABLET} {
    gap: 24px;
  }
`

Page.PlanCard = styled.div`
  box-sizing: border-box;
  width: 340px;
  border: 2px solid ${p => p.theme.primaryLighter};
  border-radius: 10px;
  padding: 20px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  /* Subtract half the 24px row gap from each card so two fit per row. */
  ${TABLET} {
    width: calc(50% - 12px);
  }
  ${MOBILE} {
    width: 100%;
    max-width: 335px;
  }
`

Page.PlanArt = styled.img`
  width: 70px;
  height: 70px;
  object-fit: contain;
  margin-bottom: 8px;
`

Page.PlanTitle = styled.div`
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.mediumGray};
  margin-bottom: 10px;
`

Page.PlanSmallNote = styled.div`
  font-size: 11px;
  color: ${p => p.theme.lightestGray};
  margin-bottom: 6px;
`

Page.PlanDesc = styled.div`
  font-size: 16px;
  color: ${p => p.theme.mediumGray};
  max-width: 251px;
  margin-bottom: 9px;
`

Page.PlanLearnMore = styled.a`
  font-size: 16px;
  color: ${p => p.theme.primaryColor};
  cursor: pointer;
  text-decoration: underline;
  margin-bottom: 40px;
`

// Price + CTA lock-up. `margin-top: auto` pins it to the bottom of the card so the buttons line
// up across cards whose descriptions differ in height (the cards themselves already stretch to a
// common height via PlanRow). The 40px margin under "Learn more" stays the minimum gap.
Page.PlanFooter = styled.div`
  margin-top: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

Page.PlanPrice = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  & .currency {
    font-size: 20px;
    font-weight: 600;
    color: ${p => p.theme.lightestGray};
  }
  & .amount {
    font-size: 20px;
    font-weight: 600;
    color: ${p => p.theme.darkestGray};
  }
  & .interval {
    font-size: 16px;
    font-weight: normal;
    color: ${p => p.theme.mediumGray};
  }
  & .asterisk {
    font-size: 16px;
    font-weight: normal;
    color: ${p => p.theme.mediumGray};
  }
`

Page.PlanPriceNote = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.mediumGray};
  margin-top: 4px;
`

Page.PlanOrgPays = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.mediumGray};
`

// Blue pill CTA on each card (200x41, radius 26).
Page.PlanCta = styled.button`
  margin-top: 30px;
  width: 200px;
  max-width: 100%;
  height: 41px;
  border: none;
  border-radius: 26px;
  background: ${p => p.theme.primaryColor};
  color: #ffffff;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    filter: brightness(0.92);
  }
`

// Bottom "Still not sure?" contact panel (desktop only).
Page.ContactSection = styled.div`
  width: 100%;
  margin-top: 80px;
  margin-bottom: 40px;
  @media (max-width: 800px) {
    display: none;
  }
`

Page.ContactHeading = styled.div`
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 45px;
  font-weight: 400;
  color: ${p => p.theme.mediumGray};
  margin-bottom: 16px;
`

Page.ContactRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
`

Page.ContactLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 520px;
`

Page.ContactText = styled.div`
  font-size: 16px;
  color: ${p => p.theme.mediumGray};
  line-height: 1.5;
  margin-bottom: 28px;
`

Page.ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 41px;
  border-radius: 20.5px;
  background: ${p => p.theme.mediumGray};
  color: #ffffff;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: 0.2s;
  &:hover {
    filter: brightness(0.9);
  }
`

Page.ContactPanel = styled.div`
  background: ${p => p.theme.ternaryColor};
  border-radius: 25px;
  padding: 12px 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  & img {
    width: 200px;
    max-width: 100%;
    height: auto;
  }
`

// Mobile-only contact chip; the whole chip links to the contact page.
Page.ContactChip = styled.a`
  display: none;
  @media (max-width: 800px) {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    background: ${p => p.theme.cardHoverBgColor};
    border-radius: 10px;
    padding: 18px 10px 18px 14px;
    margin-top: 40px;
    text-decoration: none;
    color: ${p => p.theme.darkestGray};
  }
  & img {
    width: 50px;
    height: 50px;
    object-fit: contain;
    flex-shrink: 0;
  }
  & span {
    flex: 1;
    font-size: 16px;
  }
`

// Shows the contact panel on desktop and a linked chip on mobile.
const ContactBlock = () => {
  const theme = useTheme()

  return (
    <>
      <Page.ContactSection>
        <Page.ContactRow>
          <Page.ContactLeft>
            <Page.ContactHeading>Still not sure?</Page.ContactHeading>
            <Page.ContactText>
              Have any questions? We’re available M-F 8am-5pm PST at (800) 774-7459, or anytime at{' '}
              <Page.Link href='mailto:help@guidedchoice.com'>help@guidedchoice.com</Page.Link>.
            </Page.ContactText>
            <Page.ContactButton href={CONTACT_URL} target='_blank' rel='noopener noreferrer'>
              Contact us
            </Page.ContactButton>
          </Page.ContactLeft>
          <Page.ContactPanel>
            <img src={ContactArt} alt='' />
          </Page.ContactPanel>
        </Page.ContactRow>
      </Page.ContactSection>

      <Page.ContactChip href={CONTACT_URL} target='_blank' rel='noopener noreferrer'>
        <img src={AdvisorArt} alt='' />
        <span>Have any questions? Set up a time to talk with us.</span>
        <SVGWrapper svg={IpmArrowRight} size='large' fill={theme.primaryColor} margin='0px' />
      </Page.ContactChip>
    </>
  )
}

Page.ContactBlock = ContactBlock

export default Page
